import React, { useState, useEffect, Fragment } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft } from '@fortawesome/free-solid-svg-icons';
import { GoogleMap, useJsApiLoader } from "@react-google-maps/api";
import AdminHeaderNew from '../../NewAdminPanel/Common/AdminHeader';
import AdminFooterNew from '../../NewAdminPanel/Common/AdminFooter';
import AdminSideBarNew from '../../NewAdminPanel/Common/AdminSidebarMnt';

const containerStyle = {
  width: "100%",
  height: "50vh",
};

const center = {
  lat: 28.641832439372656,
  lng: 77.22480777651072,
};

const parseCoordinate = (value) => {
  const num = Number(value);
  return Number.isFinite(num) ? num : null;
};

const getPolygonShapeFromData = (data) => {
  if (!data || typeof data !== 'object') return null;
  return (
    data.strPolygonShape ||
    data.polygonShape ||
    data.polygonshape ||
    data.strpolygonshape ||
    null
  );
};

const normalizePolygonCoordinates = (rawPolygonShape) => {
  if (!rawPolygonShape) return [];

  let parsed = rawPolygonShape;
  if (typeof rawPolygonShape === 'string') {
    try {
      parsed = JSON.parse(rawPolygonShape);
    } catch (error) {
      return [];
    }
  }

  const points = Array.isArray(parsed?.[0]) ? parsed[0] : parsed;
  if (!Array.isArray(points)) return [];

  return points
    .map((point) => {
      const lat = parseCoordinate(point?.lat ?? point?.latitude);
      const lng = parseCoordinate(point?.lng ?? point?.longitude ?? point?.long);
      if (lat === null || lng === null) return null;
      return { lat, lng };
    })
    .filter(Boolean);
};

const parseImagePaths = (value) => {
  if (!value) return [];
  return String(value)
    .split(',')
    .map((path) => path.trim())
    .filter(Boolean);
};

const getCanonicalImageKey = (imagePath) => {
  if (!imagePath || typeof imagePath !== 'string') return '';
  return imagePath.trim().replace(/\\/g, '/').replace(/^\/+/, '').toLowerCase();
};

const buildImageUrl = (imagePath) => {
  if (!imagePath || typeof imagePath !== 'string') return null;

  const trimmedPath = imagePath.trim();
  if (!trimmedPath) return null;

  if (
    trimmedPath.toLowerCase().startsWith('http://') ||
    trimmedPath.toLowerCase().startsWith('https://') ||
    trimmedPath.startsWith('//')
  ) {
    return trimmedPath;
  }

  const baseUrl = (
    process.env.NODE_ENV === 'development'
      ? process.env.REACT_APP_DEV_MODE
      : process.env.REACT_APP_PRO_MODE
  )?.replace(/\/+$/, '');

  if (!baseUrl) return trimmedPath;

  const normalizedPath = trimmedPath.startsWith('/') ? trimmedPath : `/${trimmedPath}`;
  return `${baseUrl}/api/common/GetAttachbyURL?imageURL=${encodeURIComponent(normalizedPath)}`;
};

const normalizeImageData = (rawImageData) => {
  if (!rawImageData) return [];

  const rows = Array.isArray(rawImageData) ? rawImageData : [rawImageData];
  const normalized = [];

  rows.forEach((row) => {
    if (!row) return;

    // Use only the first populated primary image field to avoid duplicating
    // the same file when backend sends equivalent values in multiple fields.
    const primaryFieldCandidates = [
      row.imagepath,
      row.imagePath,
      row.attPath,
      row.imgPath,
    ];

    const firstPrimaryField = primaryFieldCandidates.find(
      (value) => parseImagePaths(value).length > 0
    );

    const selectedPaths = firstPrimaryField
      ? parseImagePaths(firstPrimaryField)
      : [
        ...parseImagePaths(row.imagename),
        ...parseImagePaths(row.imageName),
      ];

    const uniquePaths = [];
    const seenPathKeys = new Set();

    selectedPaths.forEach((pathValue) => {
      const canonicalKey = getCanonicalImageKey(pathValue);
      if (!canonicalKey || seenPathKeys.has(canonicalKey)) return;
      seenPathKeys.add(canonicalKey);
      uniquePaths.push(pathValue);
    });

    uniquePaths.forEach((pathValue, index) => {
      normalized.push({
        ...row,
        _pathIndex: index,
        canonicalImageKey: getCanonicalImageKey(pathValue),
        resolvedImageSrc: buildImageUrl(pathValue),
      });
    });
  });

  const dedupedBySrc = [];
  const seenSrc = new Set();

  normalized
    .filter((img) => img?.resolvedImageSrc)
    .forEach((img) => {
      const dedupeKey = img.canonicalImageKey || img.resolvedImageSrc;
      if (seenSrc.has(dedupeKey)) return;
      seenSrc.add(dedupeKey);
      dedupedBySrc.push(img);
    });

  return dedupedBySrc;
};

const SurveyorDetailView = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { farmerData: initialFarmerData } = location.state || {};
  const initialPolygonShape = getPolygonShapeFromData(initialFarmerData);
  const beneficiaryId = initialFarmerData?.beneficiaryId ?? initialFarmerData?.beneficiaryID ?? initialFarmerData?.beneficiaryid ?? initialFarmerData?.beneficiary_id ?? initialFarmerData?.id ?? initialFarmerData?.Id;
  const farmerCode = initialFarmerData?.farmerCode;
  const [polygonData, setPolygonData] = useState(
    initialPolygonShape
      ? { ...initialFarmerData, strPolygonShape: initialPolygonShape }
      : null
  );
  const [farmerData, setFarmerData] = useState(initialFarmerData || null);
  const [imageData, setImageData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeImage, setActiveImage] = useState();
  const [map, setMap] = useState(null);

  const polygonCoordinates = React.useMemo(
    () => normalizePolygonCoordinates(polygonData?.strPolygonShape || polygonData?.polygonShape),
    [polygonData]
  );

  const fallbackLat = parseCoordinate(
    polygonData?.surveyorLat ?? polygonData?.imageLat ?? farmerData?.surveyorLat ?? farmerData?.imageLat
  );
  const fallbackLng = parseCoordinate(
    polygonData?.surveyorLong ?? polygonData?.imageLong ?? farmerData?.surveyorLong ?? farmerData?.imageLong
  );
  const hasPolygonShape = polygonCoordinates.length > 0;
  const hasMapLocation = hasPolygonShape || (fallbackLat !== null && fallbackLng !== null);
  const mapCenter = fallbackLat !== null && fallbackLng !== null
    ? { lat: fallbackLat, lng: fallbackLng }
    : center;

  const { isLoaded } = useJsApiLoader({
    id: "google-map-script",
    googleMapsApiKey: "AIzaSyD4X4kzlpReymgcXUXGh0Q3TalmxJmasM0",
  });



  const onLoad = React.useCallback(function callback(mapInstance) {
    setMap(mapInstance);

    if (polygonCoordinates.length > 0) {
      try {
        const polygon = new window.google.maps.Polygon({
          paths: polygonCoordinates,
          strokeColor: "#FF0000",
          strokeOpacity: 1,
          strokeWeight: 2,
          fillColor: "#FF0000",
          fillOpacity: 0.1,
        });

        polygon.setMap(mapInstance);

        // Create bounds to center the map
        const bounds = new window.google.maps.LatLngBounds();
        polygonCoordinates.forEach(coord => {
          bounds.extend(new window.google.maps.LatLng(coord.lat, coord.lng));
        });

        mapInstance.fitBounds(bounds);

        // Add marker at center
        new window.google.maps.Marker({
          position: bounds.getCenter(),
          map: mapInstance,
          title: "Center",
        });
      } catch (error) {
        console.error('Error creating polygon:', error);
      }
    } else if (fallbackLat !== null && fallbackLng !== null) {
      mapInstance.setCenter({ lat: fallbackLat, lng: fallbackLng });
      mapInstance.setZoom(18);
      new window.google.maps.Marker({
        position: { lat: fallbackLat, lng: fallbackLng },
        map: mapInstance,
        title: "Surveyor Location",
      });
    }
  }, [polygonCoordinates, fallbackLat, fallbackLng]);

  const onUnmount = React.useCallback(function callback(map) {
    setMap(null);
  }, []);

  const handleImageClick = (index) => {
    setActiveImage(index);
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        // Farmer details are already available from navigation state

        // Only fetch polygon and image data if we have a farmerCode (CRM data)
        // Individual Survey data may not have this
        if (farmerCode) {
          // Fetch polygon data
          const polyResponse = await axios.post(
            `${process.env.NODE_ENV === "development" ? process.env.REACT_APP_DEV_MODE : process.env.REACT_APP_PRO_MODE}/api/MT/Farmers/GetMTFarmerPolyData`,
            {
              farmerCode: farmerCode
            },
            {
              headers: {
                'accept': '*/*',
                'Content-Type': 'application/json'
              }
            }
          );

          if (polyResponse.data.success && polyResponse.data.dataContent?.length > 0) {
            setPolygonData(polyResponse.data.dataContent[0]);
          }

          // Fetch image data
          const imageResponse = await axios.post(
            `${process.env.NODE_ENV === "development" ? process.env.REACT_APP_DEV_MODE : process.env.REACT_APP_PRO_MODE}/api/MT/Farmers/GetMTFarmerImageData`,
            {
              farmerCode: farmerCode
            },
            {
              headers: {
                'accept': '*/*',
                'Content-Type': 'application/json'
              }
            }
          );

          if (imageResponse.data.success && imageResponse.data.dataContent?.length > 0) {
            setImageData(normalizeImageData(imageResponse.data.dataContent));
          } else {
            setImageData([]);
          }
        } else {
          // For Individual Survey or other data without farmerCode, 
          // fetch by beneficiaryId when available, otherwise show passed state data.
          if (beneficiaryId) {
            const benResponse = await axios.post(
              `${process.env.NODE_ENV === "development" ? process.env.REACT_APP_DEV_MODE : process.env.REACT_APP_PRO_MODE}/api/ExpensiveMachinery/GetExpBenDataByBenID`,
              {
                beneficiaryId: beneficiaryId
              },
              {
                headers: {
                  'accept': '*/*',
                  'Content-Type': 'application/json'
                }
              }
            );

            if (benResponse.data.success && benResponse.data.dataContent) {
              const beneficiaryData = Array.isArray(benResponse.data.dataContent)
                ? benResponse.data.dataContent[0]
                : benResponse.data.dataContent;

              const mergedData = {
                ...(initialFarmerData || {}),
                ...(beneficiaryData || {}),
              };

              const mergedPolygonShape =
                getPolygonShapeFromData(beneficiaryData) ||
                getPolygonShapeFromData(initialFarmerData);

              if (mergedPolygonShape) {
                setPolygonData({
                  ...mergedData,
                  strPolygonShape: mergedPolygonShape,
                  polygonShape: mergedPolygonShape,
                });
              }

              setFarmerData(mergedData || initialFarmerData);
              setImageData(normalizeImageData(beneficiaryData || mergedData));
            } else {
              setImageData(normalizeImageData(initialFarmerData));
            }
          } else {
            setImageData(normalizeImageData(initialFarmerData));
          }

          console.log('No farmerCode available - showing available farmer data only', { beneficiaryId, initialFarmerData });
        }
      } catch (error) {
        console.error('Error fetching surveyor details:', error);
      } finally {
        setLoading(false);
      }
    };

    if (farmerCode || beneficiaryId || initialFarmerData) {
      fetchData();
    } else {
      // No useful incoming data, stop loader.
      setLoading(false);
    }
  }, [farmerCode, beneficiaryId, initialFarmerData]);

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '200px' }}>
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  return (
    <>
      <AdminHeaderNew />
      <AdminSideBarNew />
      <main id="main" className="main">
         <div className="pagetitle">
           <div className="d-flex justify-content-between align-items-center">
             <div>
               <h1>Surveyor Details</h1>
               <nav>
                 <ol className="breadcrumb">
                   <li className="breadcrumb-item">Home</li>
                   <li className="breadcrumb-item active">Surveyor Details</li>
                 </ol>
               </nav>
             </div>
             <button
               type="button"
               className="btn btn-success"
               onClick={() => {
                const { returnTo, expandedRows, expandedSurveyorRows, surveyorDetailsByDistrict, farmerDetailsBySurveyor } = location.state || {};
                if (returnTo) {
                  navigate(returnTo || "/MNT/dashboardMnT", {
                    state: {
                      expandedRows,
                      expandedSurveyorRows,
                      surveyorDetailsByDistrict,
                      farmerDetailsBySurveyor,
                      restoreState: true,
                      crmDemoVisible: location.state?.crmDemoVisible,
                      chcHistoryVisible: location.state?.chcHistoryVisible,
                      crmDemoDetails: location.state?.crmDemoDetails,
                      chcDetails: location.state?.chcDetails,
                      expensiveCrmVisible: location.state?.expensiveCrmVisible,
                      expensiveCrmDetails: location.state?.expensiveCrmDetails
                    }
                  });
                } else {
                  navigate(-1);
                }
              }}
             >
               <i>
                 <FontAwesomeIcon icon={faArrowLeft} />
               </i>{" "}
               Back
             </button>
           </div>
         </div>
         <section className="section">
            <div className="row">
              <div className="col-lg-12 dashboard">
                <div className="card">
                  <div className="card-body p-5 pt-2">
                    {/* {isLoaded && hasMapLocation && (
                      <>
                        <div className="row cropview">
                          <h5 className="formgrouping">Polygon Details</h5>
                          <GoogleMap
                            mapContainerStyle={containerStyle}
                            center={mapCenter}
                            zoom={18}
                            onLoad={onLoad}
                            onUnmount={onUnmount}
                            mapTypeId="satellite"
                          ></GoogleMap>
                        </div>
                      </>
                    )} */}

                    {farmerData && (
                      <div className="row cropview">
                        <h5 className="formgrouping">Farmer Details</h5>
                        <div className="row surveydetailsbg">
                          <div className="col-md-3">
                            <h6>Farmer Name</h6>
                            <p>{farmerData?.farmerName || 'N/A'}</p>
                          </div>
                          <div className="col-md-3">
                            <h6>State</h6>
                            <p>{farmerData?.stateName || 'N/A'}</p>
                          </div>
                          <div className="col-md-3">
                            <h6>District</h6>
                            <p>{farmerData?.districtName || 'N/A'}</p>
                          </div>
                          <div className="col-md-3">
                            <h6>Village</h6>
                            <p>{farmerData?.villageName || 'N/A'}</p>
                          </div>
                          <div className="col-md-3">
                            <h6>Mobile No.</h6>
                            <p>{farmerData?.mobileNo || farmerData?.mobileNumber || 'N/A'}</p>
                          </div>
                          <div className="col-md-3">
                            <h6>Aadhaar No.</h6>
                            <p>{farmerData?.aadharNo || farmerData?.aadhaarNo || 'N/A'}</p>
                          </div>
                        </div>
                        <div className="row surveydetailsbg">
                          <div className="col-md-3">
                            <h6>Father / Husband Name</h6>
                            <p>{farmerData?.fatherHusbandName || 'N/A'}</p>
                          </div>
                          <div className="col-md-3">
                            <h6>Gender</h6>
                            <p>{farmerData?.genderName || 'N/A'}</p>
                          </div>
                          <div className="col-md-3">
                            <h6>Farmer Type</h6>
                            <p>{farmerData?.farmerTypeName || 'N/A'}</p>
                          </div>
                          <div className="col-md-3">
                            <h6>Caste</h6>
                            <p>{farmerData?.casteCategoryName || 'N/A'}</p>
                          </div>
                        </div>
                        <div className="row surveydetailsbg">
                          <div className="col-md-3">
                            <h6>Financial Year</h6>
                            <p>{farmerData?.financialYear || 'N/A'}</p>
                          </div>
                          <div className="col-md-3">
                            <h6>Address</h6>
                            <p>{farmerData?.fAddress || 'N/A'}</p>
                          </div>
                          <div className="col-md-3">
                            <h6>Land Details</h6>
                            <p>{farmerData?.landDetails || 'N/A'}</p>
                          </div>
                          <div className="col-md-3">
                            <h6>Khasra / Survey No.</h6>
                            <p>{farmerData?.khasraNoSurveyNo || farmerData?.surveyNo || 'N/A'}</p>
                          </div>
                        </div>
                        <div className="row surveydetailsbg">
                          <div className="col-md-3">
                            <h6>Khata</h6>
                            <p>{farmerData?.khata || 'N/A'}</p>
                          </div>
                          <div className="col-md-3">
                            <h6>Created On</h6>
                            <p>{farmerData?.createdOn ? new Date(farmerData.createdOn).toLocaleString() : 'N/A'}</p>
                          </div>
                          <div className="col-md-3">
                            <h6>Scheme</h6>
                            <p>{farmerData?.schemeName || 'N/A'}</p>
                          </div>
                          <div className="col-md-3">
                            <h6>Component</h6>
                            <p>{farmerData?.componentName || 'N/A'}</p>
                          </div>
                        </div>
                      </div>
                    )}

                    {hasMapLocation && (
                      <div className="row cropview">
                        <h5 className="formgrouping">Survey Details</h5>
                        <div className="row surveydetailsbg">
                          <div className="col-md-3">
                            <h6>Farmer Name</h6>
                            <p>{polygonData?.farmerName || farmerData?.farmerName || 'N/A'}</p>
                          </div>
                          {/* <div className="col-md-3">
                            <h6>Plot ID</h6>
                            <p>{polygonData.plotId || 'N/A'}</p>
                          </div> */}
                          <div className="col-md-3">
                            <h6>Surveyor Location</h6>
                            <p>
                              Lat: {polygonData?.surveyorLat || farmerData?.surveyorLat || farmerData?.imageLat || 'N/A'}, 
                              Long: {polygonData?.surveyorLong || farmerData?.surveyorLong || farmerData?.imageLong || 'N/A'}
                            </p>
                          </div>
                        </div>
                      </div>
                    )}

                    {imageData.length > 0 && (
                      <div className="row cropview">
                        <h5 className="formgrouping">Image Details</h5>
                        {imageData.map((image, index) => (
                          <Fragment key={index}>
                            <div className="col-md-2">
                              <img
                                src={image.resolvedImageSrc || image.imagePath || image.attPath}
                                alt={`Survey ${index + 1}`}
                                data-bs-toggle="modal"
                                data-bs-target="#staticBackdrop"
                                className="img-fluid imgdetials"
                                onClick={() => handleImageClick(index)}
                              />
                            </div>
                            {/* <div className="col-md-4">
                              <div className="row imgdetialsbg">
                                <div className="col-md-6">
                                  <h6>Latitude</h6>
                                  <p>{image.surveyorLat || image.imageLat || farmerData?.imageLat || 'N/A'}</p>
                                </div>
                                <div className="col-md-6">
                                  <h6>Longitude</h6>
                                  <p>{image.surveyorLong || image.imageLong || farmerData?.imageLong || 'N/A'}</p>
                                </div>
                              </div>
                            </div> */}
                          </Fragment>
                        ))}
                      </div>
                    )}

                    {!hasMapLocation && !imageData.length && !farmerData && (
                      <div className="alert alert-info">
                        No data available for this record.
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </section>
      </main>
      <AdminFooterNew />

      {/* Image Modal */}
      {imageData.length > 0 && (
        <div
          className="modal fade"
          id="staticBackdrop"
          data-bs-backdrop="static"
          data-bs-keyboard="false"
          tabIndex="-1"
          aria-labelledby="staticBackdropLabel"
          aria-hidden="true"
        >
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title" id="staticBackdropLabel">
                  Image
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  data-bs-dismiss="modal"
                  aria-label="Close"
                >
                  X
                </button>
              </div>
              <div className="modal-body">
                {activeImage !== undefined && (
                  <img
                    src={imageData[activeImage].resolvedImageSrc || imageData[activeImage].imagePath || imageData[activeImage].attPath}
                    alt={`Survey ${activeImage + 1}`}
                    className="img-fluid w-100"
                  />
                )}
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  data-bs-dismiss="modal"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default SurveyorDetailView;
